import { GameRoomState } from "@presidents/game-engine";
import { Socket } from "socket.io-client";
import { playerInGameRoom } from "../components/utils";
import { User } from "../context/UserContext";

type JoinState = "idle" | "joining" | "joined" | "leaving";

type GameRoomStateListener = (gameRoomState: GameRoomState) => void;

export class GameRoomController {
  public roomId: string;
  private user: User;
  private transport: Socket;
  private roomState: GameRoomState | null = null;

  private joinState: JoinState = "idle";

  private listeners = new Set<GameRoomStateListener>();

  constructor(
    roomId: string,
    user: User,
    transport: Socket,
    initialState?: GameRoomState | null
  ) {
    this.roomId = roomId;
    this.user = user;
    this.transport = transport;
    if (initialState) {
      this.roomState = initialState;
    }
    transport.on("gamestate", this.handleStateChange);
    transport.on("connect", this.connect);
    this.connect();
  }

  public destroy = () => {
    console.log("Calling destroy");
    this.joinState = "idle";
    this.transport.off("gamestate", this.handleStateChange);
    this.transport.off("connect", this.connect);
  };

  private connect = () => {
    if (this.joinState !== "idle") {
      this.join();
    } else {
      this.wait();
    }
  };

  public wait() {
    this.transport.emit("wait", { roomId: this.roomId });
  }

  public join() {
    if (this.joinState !== "joined") {
      this.joinState = "joining";
    }
    this.transport.emit("join", { roomId: this.roomId });
  }

  get ready() {
    return !!this.roomState;
  }

  get hasJoined() {
    return this.joinState === "joined";
  }

  get players() {
    return this.roomState?.players;
  }

  get playerRanks() {
    return this.roomState?.playerRanks;
  }

  get leaderboard() {
    return this.roomState?.leaderBoard;
  }

  get state() {
    return this.roomState;
  }

  public listen = (fn: GameRoomStateListener) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  public emit = () => {
    this.listeners.forEach((fn) => fn(this.roomState as GameRoomState));
  };

  private handleStateChange = (nextState: GameRoomState) => {
    if (
      this.joinState === "joining" &&
      playerInGameRoom(this.user.uid, nextState.players)
    ) {
      this.joinState = "joined";
    }
    this.roomState = nextState;
    this.emit();
  };

  public newGame() {
    this.transport.emit("newgame", { roomId: this.roomId });
  }
}
